/**
 * Claro u oscuro · 24-09-2026
 *
 * ## Una elección por navegador, y sin ella, la del sistema
 *
 * Se guarda en `localStorage` y no en la cuenta: es de la pantalla, no de la
 * persona —el portátil de la oficina en claro, el móvil de noche en oscuro—. Y
 * mientras nadie elige, se sigue al sistema **en vivo**: si el sistema cambia a las
 * ocho de la tarde, el panel cambia con él.
 *
 * ## El atributo lo pone primero `index.html`
 *
 * Un guion en línea lee lo mismo que esto y pone `data-tema` en `<html>` **antes**
 * de que React monte. Sin él, quien usa el oscuro vería un destello blanco en cada
 * carga. Los dos leen la misma clave, `CLAVE`: si se cambia aquí, se cambia allí.
 *
 * ## `localStorage` puede fallar
 *
 * En una ventana privada o con los datos del sitio bloqueados, leer o escribir
 * lanza. Se trata como «no hay elección»: el modo sigue funcionando durante la
 * visita, solo que no se recuerda.
 */
import { useSyncExternalStore } from 'react';

export type Aspecto = 'claro' | 'oscuro';

export const CLAVE = 'kiap.aspecto';

const CONSULTA = '(prefers-color-scheme: dark)';

const oyentes = new Set<() => void>();

function guardado(): Aspecto | null {
  try {
    const valor = localStorage.getItem(CLAVE);
    return valor === 'claro' || valor === 'oscuro' ? valor : null;
  } catch {
    return null;
  }
}

function delSistema(): Aspecto {
  return window.matchMedia(CONSULTA).matches ? 'oscuro' : 'claro';
}

/** La elección de la visita, cuando `localStorage` no deja guardarla. */
let enMemoria: Aspecto | null = null;

function actual(): Aspecto {
  return guardado() ?? enMemoria ?? delSistema();
}

function aplicar(): void {
  document.documentElement.dataset.tema = actual();
  for (const oyente of oyentes) oyente();
}

const hayNavegador = typeof window !== 'undefined';

if (hayNavegador) {
  // El sistema cambia de modo: solo importa si nadie ha elegido.
  window.matchMedia(CONSULTA).addEventListener('change', aplicar);

  // Otra pestaña eligió: esta se pone igual, que es lo que se espera.
  window.addEventListener('storage', (evento) => {
    if (evento.key === CLAVE) aplicar();
  });
}

export function elegirAspecto(aspecto: Aspecto): void {
  enMemoria = aspecto;
  try {
    localStorage.setItem(CLAVE, aspecto);
  } catch {
    // Ver la cabecera: se queda en memoria.
  }
  aplicar();
}

function suscribir(oyente: () => void): () => void {
  oyentes.add(oyente);
  return () => oyentes.delete(oyente);
}

/** El modo en que se está pintando. Se vuelve a pintar cuando cambia. */
export function useAspecto(): Aspecto {
  return useSyncExternalStore(suscribir, actual);
}

// Por si `index.html` no llegó a ponerlo (una entrada nueva, una recarga en caliente).
if (hayNavegador) aplicar();
